/**
 * Slot-level codec of the original save string (format v45, docs/analysis/vk-and-network.md §4.2).
 *
 * The backend never uses this as its working format (profiles are JSON); it is only used to import old saves
 * (`profile.importLegacy`) and as a test fixture format. Mapping all slots to the game's `player` object is
 * the job of the ported `read_storage_45` in src/game; this module validates integrity and exposes the few
 * scalar fields the backend needs for summaries (leaderboards).
 *
 * - chars 0..3967: base-60 digits (`FWP.encode_param` / `decode_param`, FWP.as:16851-16885);
 * - chars 3968..3999: MD5 (lowercase hex, UTF-8) of chars 0..3967 (FWP.as:17006-17018), no salt;
 * - a 2000-char string (older format) is padded with `A` to 4000 and is not MD5-checked (FWP.as:16990-16999);
 * - slot 249 is the version marker; `'t'` (= 45) selects `read_storage_45` (FWP.as:16905).
 */
import { md5Hex } from '../crypto/md5';

export const LEGACY_LENGTH = 4000;
export const LEGACY_SHORT_LENGTH = 2000;
export const LEGACY_PAYLOAD_LENGTH = 3968;
export const LEGACY_VERSION_SLOT = 249;
export const LEGACY_VERSION_45 = 45;

/** FWP.encode_param: 0..25 -> A..Z, 26..51 -> a..z, 52..59 -> 0..7, anything else -> 'A'. */
export function encodeLegacyDigit(n: number): string {
  if (n < 26) return String.fromCharCode(65 + n);
  if (n < 52) return String.fromCharCode(97 - 26 + n);
  if (n < 60) return String.fromCharCode(48 - 52 + n);
  return 'A';
}

/** FWP.decode_param: also accepts '8'/'9' (60/61); every other character decodes to 0. */
export function decodeLegacyDigit(ch: string): number {
  const c = ch.charCodeAt(0);
  if (c >= 48 && c <= 57) return c + 4;
  if (c >= 65 && c <= 90) return c - 65;
  if (c >= 97 && c <= 122) return c - 71;
  return 0;
}

/** Little-endian base-60 number of `digits` slots starting at `start`. */
export function readLegacyNumber(slots: readonly number[], start: number, digits: number): number {
  let v = 0;
  let mul = 1;
  for (let i = 0; i < digits; i++) {
    v += (slots[start + i] ?? 0) * mul;
    mul *= 60;
  }
  return v;
}

/** Writes a little-endian base-60 number (values that do not fit are truncated to the low digits). */
export function writeLegacyNumber(slots: number[], start: number, digits: number, value: number): void {
  let v = Math.max(0, Math.floor(value));
  for (let i = 0; i < digits; i++) {
    slots[start + i] = v % 60;
    v = Math.floor(v / 60);
  }
}

export function slotsToPayload(slots: readonly number[]): string {
  let s = '';
  for (let i = 0; i < LEGACY_PAYLOAD_LENGTH; i++) s += encodeLegacyDigit(slots[i] ?? 0);
  return s;
}

/** Appends the MD5 integrity suffix to a 3968-char payload. */
export function signLegacyPayload(payload: string): string {
  if (payload.length !== LEGACY_PAYLOAD_LENGTH) throw new Error(`legacy payload must be ${LEGACY_PAYLOAD_LENGTH} chars`);
  return payload + md5Hex(payload);
}

export type LegacyFormat = 'v45' | 'v45-short';

export type LegacyInspection =
  | { ok: true; format: LegacyFormat; version: number; md5Checked: boolean; slots: number[]; normalized: string }
  | { ok: false; reason: 'empty' | 'length' | 'charset' | 'checksum' | 'version'; message: string };

const LEGACY_CHARSET = /^[A-Za-z0-9]*$/;

/**
 * Validates a legacy save string.
 * // PORT: the original turned an MD5 mismatch into a permanent "101 Cheating" ban and reset the profile
 * // (defect D2, FWP.as:17012-17018); the backend reports `checksum` instead and changes nothing.
 * Versions other than 45 ('P'/'o', FWP.as:16926-16933) are rejected: their migration is UNVERIFIED.
 */
export function inspectLegacyStorage(input: string): LegacyInspection {
  if (input.length === 0) return { ok: false, reason: 'empty', message: 'Legacy save is empty' };
  if (input.length !== LEGACY_LENGTH && input.length !== LEGACY_SHORT_LENGTH) {
    return { ok: false, reason: 'length', message: `Legacy save must be ${LEGACY_LENGTH} or ${LEGACY_SHORT_LENGTH} characters, got ${input.length}` };
  }
  const short = input.length === LEGACY_SHORT_LENGTH;
  const payloadPart = short ? input : input.slice(0, LEGACY_PAYLOAD_LENGTH);
  if (!LEGACY_CHARSET.test(payloadPart)) return { ok: false, reason: 'charset', message: 'Legacy save contains characters outside [A-Za-z0-9]' };
  const normalized = short ? input + 'A'.repeat(LEGACY_LENGTH - LEGACY_SHORT_LENGTH) : input;
  if (!short) {
    const expected = md5Hex(normalized.slice(0, LEGACY_PAYLOAD_LENGTH));
    if (expected !== normalized.slice(LEGACY_PAYLOAD_LENGTH)) {
      return { ok: false, reason: 'checksum', message: 'Legacy save checksum (MD5) does not match; the save is corrupted' };
    }
  }
  const slots: number[] = new Array(LEGACY_LENGTH);
  for (let i = 0; i < LEGACY_LENGTH; i++) slots[i] = decodeLegacyDigit(normalized.charAt(i));
  const version = slots[LEGACY_VERSION_SLOT];
  if (version !== LEGACY_VERSION_45) {
    return { ok: false, reason: 'version', message: `Unsupported legacy save version marker ${version} (only 45 is supported)` };
  }
  return { ok: true, format: short ? 'v45-short' : 'v45', version, md5Checked: !short, slots, normalized };
}

/** Scalar fields the backend reads from a legacy save (slot positions from read_storage_45, FWP.as:17020-17250). */
export interface LegacyScalars {
  exp: number;
  gold: number;
  diam: number;
  rating: number;
  achievRatingBonus: number;
  assaults: number;
  attack: number;
  protect: number;
  health: number;
  worms: number;
  spec: number;
  careerMax: number;
  missionsMax: number;
  survivalMax: number;
  bossMax: number;
  lastEnter: number;
  wormNames: string[];
}

export function readLegacyScalars(slots: readonly number[]): LegacyScalars {
  const n = (start: number, digits: number) => readLegacyNumber(slots, start, digits);
  const wormNames: string[] = [];
  // worm_name1..5: 5 x 15 chars x 2 digits (char code, 0 = empty), slots 950..1099.
  for (let w = 0; w < 5; w++) {
    let name = '';
    for (let c = 0; c < 15; c++) {
      const code = n(950 + w * 30 + c * 2, 2);
      if (code !== 0) name += String.fromCharCode(code);
    }
    wormNames.push(name);
  }
  return {
    exp: n(11, 5),
    gold: n(16, 5),
    diam: n(21, 5),
    rating: n(910, 5),
    achievRatingBonus: n(578, 3),
    assaults: n(521, 3),
    attack: n(90, 2),
    protect: n(92, 2),
    health: n(94, 2),
    worms: n(96, 1),
    spec: n(63, 1),
    careerMax: n(72, 2),
    missionsMax: n(75, 2),
    survivalMax: Math.max(1, n(78, 2)),
    bossMax: Math.max(1, n(81, 2)),
    lastEnter: n(34, 6),
    wormNames,
  };
}
